import { useEffect, useMemo, useState } from 'react';
import type { Incident } from '../data/demoData';
import { apiFetch } from '../lib/apiClient';

/**
 * IncidentPlaybooksPanel
 * ----------------------
 * Renders the list of playbooks (Azure Logic Apps) configured for the current
 * customer that the analyst can fire against this incident, with one click.
 *
 * Supports four trigger flavors persisted on the saved playbook row:
 *   - http              standard manual HTTP trigger; sends incident JSON
 *   - sentinelIncident  Sentinel "incident" ApiConnectionWebhook
 *   - sentinelAlert     Sentinel "alert" ApiConnectionWebhook
 *   - sentinelEntity    Sentinel "entity" ApiConnectionWebhook — needs a
 *                       matching entity from the incident; when the playbook
 *                       declares an entity kind (e.g. Url) we only enable
 *                       the button if the incident has at least one entity
 *                       of that kind, and we render a per-entity dropdown
 *                       so the analyst picks which entity to act on.
 *
 * The panel hides itself when the customer has zero enabled playbooks (so
 * tenants that haven't configured anything see no clutter), and per-button
 * run state is local so two playbooks fired in parallel don't blow each
 * other's status away.
 */

type TriggerType = 'http' | 'sentinelEntity' | 'sentinelIncident' | 'sentinelAlert' | 'webhook';

interface SavedPlaybook {
  id: string;
  customerId: string;
  logicAppResourceId: string;
  workflowName: string;
  displayName: string;
  description: string | null;
  triggerName: string;
  triggerType: TriggerType;
  entityKind: string | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

type RunState =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'success'; message: string; at: number }
  | { kind: 'error'; message: string; at: number };

type IncidentEntity = NonNullable<Incident['entities']>[number];

interface Props {
  incident: Incident;
  customerId?: string;
  /** When true (customer_viewer role), we render the panel as informational
   * only — no Run buttons. Mirrors the AI/Investigate panel pattern. */
  readOnly?: boolean;
  /** Optional override for the entities to use when matching against entity-trigger
   * playbooks. The IncidentModal passes its `displayedEntities` (which may have
   * been filled in by the live `/incidents/:id/enrich` call) so the panel
   * "sees" the same entities the analyst sees in the modal. Without this, the
   * panel only had access to `incident.entities`, which is empty for incidents
   * fetched via the lightweight watcher / Sentinel KQL fallback path. */
  entities?: IncidentEntity[];
}

interface NormalizedEntity {
  raw: IncidentEntity;
  display: string;
  /** Sentinel canonical kind (Account / Host / Ip / Url / FileHash / etc.) */
  kind: string | null;
}

/** Normalize the dashboard's loose entity types (which can be plain strings,
 * MDE shapes, Graph shapes, KQL-projected shapes) into Sentinel canonical kinds.
 * Mirrors the server-side ENTITY_KIND_ALIASES so the UI's "is this entity
 * compatible with this entity-trigger playbook?" check matches what the
 * server will actually wrap into the Entity envelope. */
const ENTITY_ALIASES: Record<string, string> = {
  account: 'Account', user: 'Account', aaduser: 'Account', upn: 'Account',
  host: 'Host', machine: 'Host', device: 'Host', computer: 'Host',
  ip: 'Ip', ipaddress: 'Ip', ipv4: 'Ip', ipv6: 'Ip',
  url: 'Url', uri: 'Url',
  filehash: 'FileHash', hash: 'FileHash', sha256: 'FileHash', sha1: 'FileHash', md5: 'FileHash',
  mailmessage: 'MailMessage', email: 'MailMessage', message: 'MailMessage',
  process: 'Process',
  azureresource: 'AzureResource',
};

function normalizeKind(t: string | undefined): string | null {
  if (!t) return null;
  const k = t.toLowerCase().replace(/[\s_-]/g, '');
  return ENTITY_ALIASES[k] ?? null;
}

function normalizeEntities(rawEntities: IncidentEntity[]): NormalizedEntity[] {
  return rawEntities.map((raw) => {
    if (typeof raw === 'string') {
      return { raw, display: raw, kind: null };
    }
    const kind = normalizeKind(raw.type);
    // sha256-only entries are FileHash even when type is missing.
    const inferredKind = kind ?? (raw.sha256 ? 'FileHash' : null);
    return {
      raw,
      display: raw.name || raw.sha256 || `${raw.type ?? 'Entity'}`,
      kind: inferredKind,
    };
  });
}

export function IncidentPlaybooksPanel({ incident, customerId, readOnly, entities: entitiesProp }: Props) {
  const [playbooks, setPlaybooks] = useState<SavedPlaybook[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [runStates, setRunStates] = useState<Record<string, RunState>>({});
  /** For entity-trigger playbooks, the analyst's currently-selected entity
   * from the dropdown (keyed by playbook id → entity index). */
  const [selectedEntity, setSelectedEntity] = useState<Record<string, number>>({});

  // Prefer the entities the IncidentModal renders to the user (which include
  // the live-enriched alert entities). Fall back to whatever was on the
  // incident object so other call sites (e.g. tests) keep working.
  const entities = useMemo(
    () => normalizeEntities(entitiesProp ?? incident.entities ?? []),
    [entitiesProp, incident.entities],
  );

  const visible = useMemo(
    () => playbooks.filter((p) => p.enabled).sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [playbooks],
  );

  useEffect(() => {
    if (!customerId) {
      setPlaybooks([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    apiFetch<{ playbooks: SavedPlaybook[] }>(`/customers/${encodeURIComponent(customerId)}/playbooks`)
      .then((res) => {
        if (cancelled) return;
        setPlaybooks(Array.isArray(res?.playbooks) ? res.playbooks : []);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = (err as { status?: number })?.status ?? 0;
        if (status !== 401 && status !== 403) {
          setLoadError('Failed to load playbooks for this customer.');
        }
        setPlaybooks([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  /** Find entities in this incident that match a playbook's expected entity
   * kind. If the playbook didn't declare a kind (legacy row, or one our
   * heuristic couldn't infer), accept any typed entity — better to surface
   * the playbook with all entities than hide it entirely. */
  function matchingEntities(pb: SavedPlaybook): NormalizedEntity[] {
    if (!pb.entityKind) return entities.filter((e) => e.kind);
    return entities.filter((e) => e.kind === pb.entityKind);
  }

  async function runPlaybook(pb: SavedPlaybook) {
    if (!customerId) return;

    // Build the structured request body — the backend wraps it into the
    // right Sentinel envelope based on the saved triggerType. This way the
    // frontend stays oblivious to the workspace/tenant ARM context the
    // Sentinel webhook expects to see in the payload.
    const incidentPayload = {
      id: incident.id,
      incidentNumber: incident.incidentNumber,
      armIncidentName: incident.armIncidentName ?? null,
      graphIncidentId: incident.graphIncidentId ?? null,
      title: incident.title,
      description: incident.description ?? null,
      severity: incident.severity,
      status: incident.status,
      classification: incident.classification ?? null,
      assignedTo: incident.assignedTo ?? null,
      createdTime: incident.createdTime,
      lastUpdateTime: incident.lastUpdateTime,
      url: incident.webUrl ?? null,
      mitreTactics: incident.mitreTactics ?? [],
      entities: incident.entities ?? [],
      alertsCount: incident.alertsCount ?? 0,
    };

    let entityForRun: { type: string; name: string; sha256?: string; verdict?: string } | undefined;
    if (pb.triggerType === 'sentinelEntity') {
      const matches = matchingEntities(pb);
      const idx = selectedEntity[pb.id] ?? 0;
      const picked = matches[idx];
      if (!picked) {
        setRunStates((prev) => ({
          ...prev,
          [pb.id]: {
            kind: 'error',
            message: `Incident has no ${pb.entityKind ?? 'compatible'} entity to run this against.`,
            at: Date.now(),
          },
        }));
        return;
      }
      const raw = picked.raw;
      entityForRun = typeof raw === 'string'
        ? { type: pb.entityKind ?? 'Unknown', name: raw }
        : { type: raw.type, name: raw.name, sha256: raw.sha256, verdict: raw.verdict };
    }

    setRunStates((prev) => ({ ...prev, [pb.id]: { kind: 'running' } }));
    try {
      const res = await apiFetch<{ ok: boolean; runStatus: number; workflowName: string }>(
        `/customers/${encodeURIComponent(customerId)}/playbooks/${encodeURIComponent(pb.id)}/run`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ incident: incidentPayload, entity: entityForRun }),
        },
      );
      const at = Date.now();
      setRunStates((prev) => ({
        ...prev,
        [pb.id]: {
          kind: 'success',
          message: `Started - Logic App returned HTTP ${res.runStatus}.`,
          at,
        },
      }));
      setTimeout(() => {
        setRunStates((prev) => {
          const cur = prev[pb.id];
          if (cur?.kind === 'success' && cur.at === at) {
            const { [pb.id]: _drop, ...rest } = prev;
            return rest;
          }
          return prev;
        });
      }, 8_000);
    } catch (err) {
      const at = Date.now();
      const raw = (err as Error)?.message ?? 'Failed to run playbook';
      let friendly = raw;
      if (/403/.test(raw) || /Access denied/i.test(raw)) {
        friendly = 'Access denied. The principal needs Logic App Operator on this workflow.';
      } else if (/429/.test(raw)) {
        friendly = 'Azure throttled the request. Please retry in a moment.';
      } else if (/404/.test(raw)) {
        friendly = 'Playbook not found in Azure (deleted or moved).';
      }
      setRunStates((prev) => ({ ...prev, [pb.id]: { kind: 'error', message: friendly, at } }));
    }
  }

  if (!customerId) return null;
  if (loading && playbooks.length === 0) return null;
  if (visible.length === 0 && !loadError) return null;

  return (
    <div className="bg-muted/50 rounded-lg p-3 mb-4">
      <div className="flex items-baseline justify-between mb-2 gap-2 flex-wrap">
        <p className="text-xs text-muted-foreground font-semibold">
          Playbooks
          <span className="font-normal text-muted-foreground/70"> · {visible.length} configured</span>
        </p>
        <span className="text-[10px] text-muted-foreground/70">
          Triggers the Logic App with this incident's context.
        </span>
      </div>

      {loadError && (
        <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded px-2 py-1.5 mb-2">
          {loadError}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {visible.map((pb) => {
          const state = runStates[pb.id] ?? { kind: 'idle' as const };
          const isRunning = state.kind === 'running';
          const isEntity = pb.triggerType === 'sentinelEntity';
          const matches = isEntity ? matchingEntities(pb) : [];
          const noMatch = isEntity && matches.length === 0;
          const selectedIdx = selectedEntity[pb.id] ?? 0;
          const tooltip = pb.description
            ? `${pb.description}${pb.entityKind ? ` - expects ${pb.entityKind} entity` : ''}`
            : `Run playbook: ${pb.workflowName}${pb.entityKind ? ` (${pb.entityKind})` : ''}`;

          return (
            <div key={pb.id} className="flex flex-col">
              <div className="flex items-stretch gap-1">
                <button
                  type="button"
                  onClick={() => runPlaybook(pb)}
                  disabled={isRunning || readOnly || noMatch}
                  title={
                    noMatch
                      ? `Incident has no ${pb.entityKind ?? 'compatible'} entity - this playbook can't run.`
                      : tooltip
                  }
                  className="text-xs bg-card border border-border hover:border-primary hover:bg-primary/5 text-foreground px-3 py-1.5 rounded font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  {isRunning ? (
                    <>
                      <span className="animate-pulse">●</span>
                      <span>Running…</span>
                    </>
                  ) : (
                    <>
                      <span aria-hidden>▶</span>
                      <span>{pb.displayName}</span>
                      {isEntity && pb.entityKind && (
                        <span className="text-[9px] uppercase tracking-wide text-muted-foreground bg-muted px-1 py-0.5 rounded">
                          {pb.entityKind}
                        </span>
                      )}
                    </>
                  )}
                </button>
                {isEntity && matches.length > 1 && (
                  <select
                    value={selectedIdx}
                    onChange={(e) =>
                      setSelectedEntity((prev) => ({ ...prev, [pb.id]: Number(e.target.value) }))
                    }
                    disabled={isRunning || readOnly}
                    title="Pick which entity to run this playbook against"
                    className="text-[11px] bg-card border border-border rounded px-1.5 max-w-[160px] focus:outline-none focus:border-primary"
                  >
                    {matches.map((e, i) => (
                      <option key={i} value={i}>
                        {e.display.length > 28 ? `${e.display.slice(0, 28)}…` : e.display}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {isEntity && matches.length === 1 && (
                <span className="text-[10px] text-muted-foreground mt-0.5 px-1 truncate max-w-[260px]" title={matches[0].display}>
                  → {matches[0].display}
                </span>
              )}
              {state.kind === 'success' && (
                <span className="text-[10px] text-green-700 dark:text-green-400 mt-1 px-1">
                  ✓ {state.message}
                </span>
              )}
              {state.kind === 'error' && (
                <span
                  className="text-[10px] text-red-700 dark:text-red-400 mt-1 px-1 max-w-[260px] break-words"
                  title={state.message}
                >
                  ✗ {state.message}
                </span>
              )}
              {noMatch && state.kind === 'idle' && (
                <span className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5 px-1">
                  No {pb.entityKind} entity in this incident
                </span>
              )}
            </div>
          );
        })}
      </div>

      {readOnly && (
        <p className="text-[10px] text-muted-foreground/70 mt-2 italic">
          Read-only view - playbook execution is disabled for your role.
        </p>
      )}
    </div>
  );
}
